import { cva, type VariantProps } from 'class-variance-authority';

/**
 * Badge visual variants — mirror the Figma "Flashka Design System" file
 * `YZK9r3wDhvw1pY60KlVEIj`, "Badges" section (node `1749:2529`).
 *
 * Reference nodes (Primary/Neutral used as the canonical-anatomy probe per
 * type; the other colours differ only by palette):
 *   - Filled / XS:  1752:3917 (Label) · 1752:3898 (Icon+Label) ·
 *                   1752:3833 (Status) · 1752:3974 (Count) · 1752:3802 (Icon)
 *   - Ghost / XS:   1768:347 (Label) · 1768:357 (Icon+Label) ·
 *                   1768:351 (Status) · 1835:745 (Icon)   — Count is N/A for Ghost
 *   - S(28px):      1752:4500 (Icon+Label) — confirms the size ramp
 *   - Disabled:     1752:4571 — default colours + opacity-50
 *
 * Axes:
 *   colour:  neutral | primary | info | success | error | warning | reward
 *            | gold | silver | bronze — the PODIUM trio, which does not follow
 *            the `-50` fill / `-600` foreground rule the seven semantic
 *            colours share: a medal has to read as its metal, so the fill is
 *            the deck palette's own solid tint and the foreground its `-800`
 *            (see the `Podium` note under the token map).
 *            — semantic intent. Sets ONLY the soft `-50` fill (light) here; the
 *            `-600` foreground (text / icon / dot) lives in `badgeTextVariants`
 *            / `badgeDotVariants` so the inner content and the container share
 *            one source and web icons inherit it via `currentColor`.
 *   style:   filled | ghost | glass | solid — Ghost drops the background fill
 *            entirely; Glass ("Style=Outlined" in Figma, nodes 5452:44756+)
 *            keeps the soft fill but adds a 1px `-100` border, and NEUTRAL
 *            swaps its fill for translucent white (`brand/secondary/30` =
 *            #ffffffb2 ≈ white/70) so the chip reads as glass over card art.
 *            Foreground colours are identical across those three. Solid (see
 *            "Code-side additions" below) is the one style that changes the
 *            foreground: a `-500` fill with white text.
 *   type:    label | icon-label | status | count | icon — shape + content slots.
 *            `count` is Filled-only in the design (N/A for Ghost), but renders
 *            harmlessly if combined with Ghost.
 *   size:    xxs (20px) | xs (24px) | s (28px) | m (32px) | l (36px) — geometry
 *            ramp (`xxs` is a code-side addition, below).
 *   disabled: opacity treatment (`opacity-50`, per Figma node 1752:4571 — note
 *            this differs from Button's `opacity-30`, matching Chip).
 *
 * Size ramp — height / radius / font / horizontal padding all confirmed from
 * the per-size Label nodes (Filled 1752:3994/3997/3927/3950 · Ghost
 * 1768:347/482/612/741); icon-label geometry confirmed per size via
 * 1752:3939 (XS) / 3972 (S) / 3956 (M) / 3936 (L):
 *   height    24 / 28 / 32 / 36   (h-6 / h-7 / h-8 / h-9)
 *   radius     8 /  8 / 10 / 12   (Count = rounded-full at every size)
 *   font      10 / 12 / 12 / 14   (L4 / L3 / L3 / L2 — see badgeTextVariants)
 *   px         8 /  8 /  8 /  8   (label horizontal pad is FLAT across sizes)
 *   gap        4 /  4 /  4 /  4   (icon-label / status leading gap — revised
 *                                  in Figma from the original 6/8 ramp; all
 *                                  four size nodes now read gap-[4px])
 *   trail pad 12 / 16 / 16 / 16   (icon-label / status pr — only XS differs)
 *   icon      12 / 14 / 14 / 16   (icon-label; icon-ONLY is 12/14/16/18 —
 *                                  see Badge.icons.ts)
 *   dot        6 /  8 /  8 /  8   (see badgeDotVariants)
 *
 * Colour → token map (all from `brand-colors.json`):
 *   neutral → secondary | primary → primary | info → tertiary
 *   success → green | error → red | warning → orange | reward → amber
 *
 * Podium (`gold` / `silver` / `bronze`) — the leaderboard's three placed ranks,
 * added for `LeaderboardRankChip`. Solid `deck-*` / `secondary-300` fill with
 * an `-800` foreground rather than the `-50`/`-600` pair, because a soft amber
 * wash does not read as first place next to a silver and a bronze. Every one of
 * these tokens is CSS-var-driven and auto-inverts like the rest, so the trio
 * needs no `dark:` classes either.
 *
 * Dark mode: every one of these scales is CSS-var-driven and auto-inverts in
 * `.dark` (web) / `.dark:root` (native) — see tailwind-preset.cjs. The Figma
 * dark frame (2812:47477) resolves e.g. dark `*-600` foreground to the light
 * `*-400` hex and dark `*-50` fill to the light `*-950` hex — exactly the
 * palette inversion — so NO `dark:` classes are needed here.
 *
 * Code-side additions — NOT yet in the Figma badge set (Simo to confirm):
 *   size `xxs` — 20px, fully round (`rounded-full`), 11px SEMIBOLD label
 *            (not the bold L-ramp), `px-2`. Everything else follows the xs
 *            rules: flat 8 leading pad, flat 4 gap, 12 trailing pad, 6px dot;
 *            icons are 10px (icon-only = height / 2, as at every size). Born
 *            for the sign-in card's "Last used" chip, which sits on a button's
 *            top edge and must not out-weigh the button's own label.
 *   style `solid` — the `-500` fill with a WHITE foreground (text, icon, dot).
 *            `-500` is the inversion's fixed point (dark `-500` = light
 *            `-500`) and white is not a palette var, so the chip is the SAME
 *            purple / blue / … in light and dark — no `dark:` classes here
 *            either. NEUTRAL is the exception: its `-500` is a mid grey that
 *            reads as disabled, so it pairs `secondary-800` with
 *            `secondary-50` instead — both ends invert TOGETHER (a near-black
 *            chip with white text in light, a near-white chip with near-black
 *            text in dark), which keeps the contrast in both modes. The podium
 *            trio is already solid, so `solid` leaves it on its filled look.
 *            Contrast: white on the green / orange / amber `-500` is below
 *            4.5:1 — fine for a decorative marker, not for body copy.
 */
export const badgeVariants = cva(
  // Base: inline row, content-centred, non-interactive display chip. No border,
  // no shadow (flat soft fill). `inline-flex` shrinks to content; overrides the
  // View primitive's reset (`flex flex-col items-stretch`) via twMerge.
  // `shrink-0` (as on Button / Chip) keeps the chip at content size in tight
  // flex parents — the View primitive's `min-w-0` would otherwise let the fill
  // collapse under the text. `select-none` — a display chip's label is not
  // selectable content.
  'inline-flex shrink-0 select-none flex-row items-center justify-center',
  {
    variants: {
      // Background fill only (foreground colour → badgeTextVariants).
      colour: {
        // Dark steps UP (`DARK_LIFTED`): `secondary-50` sits below every content
        // surface in dark, so a neutral chip read as a black hole (review 2026-10).
        neutral: 'bg-secondary-50 dark:bg-secondary-100 ',
        primary: 'bg-primary-50 ',
        info: 'bg-tertiary-50 ',
        success: 'bg-green-50 ',
        error: 'bg-red-50 ',
        warning: 'bg-orange-50 ',
        reward: 'bg-amber-50 ',
        gold: 'bg-deck-amber ',
        silver: 'bg-secondary-300 ',
        bronze: 'bg-deck-orange ',
      },
      // Marker axis — geometry lives in the (type, size) compounds below; the
      // Count radius override is a compound too (so it wins over the size radius).
      type: {
        label: '',
        'icon-label': '',
        status: '',
        count: '',
        icon: '',
      },
      // Height + per-size radius (8 / 8 / 10 / 12, confirmed from the Label
      // nodes). Shared by all types; Count overrides radius to `rounded-full`
      // via a compound. Icon badges add the matching width to stay square.
      size: {
        // Code-side (see header): a full pill, not the 8px rectangle — at
        // 20px an 8px corner reads as a squashed tag.
        xxs: 'h-5 rounded-full',
        xs: 'h-6 rounded-[8px]',
        s: 'h-7 rounded-[8px]',
        m: 'h-8 rounded-[10px]',
        l: 'h-9 rounded-[12px]',
      },
      // Ghost drops the fill; glass adds the border; solid swaps the soft
      // fill for the `-500` (compounds below).
      style: { filled: '', ghost: '', glass: '', solid: '' },
      disabled: { true: '', false: '' },
    },
    compoundVariants: [
      // ── Ghost: clear the colour fill (transparent in both themes). Placed
      // after the colour axis so twMerge keeps `bg-transparent`. ──────────────
      { style: 'ghost', class: 'bg-transparent dark:bg-transparent' },

      // ── Glass ("Style=Outlined" in Figma): 1px `-100` border over the
      // filled `-50` fill; the border shades ride the auto-inverting palette
      // vars, so no `dark:` classes. NEUTRAL swaps its fill for translucent
      // white (`brand/secondary/30` = #ffffffb2): the `surface-raised` role at
      // 70% — white in light, #171717 (the old stock neutral-900) in dark. ──
      { style: 'glass', class: 'border' },
      {
        style: 'glass',
        colour: 'neutral',
        // Dark: the hairline is the DS `secondary-200` — `-100` sat DARKER than
        // the chip's own fill and read as a dark outline (review 2026-10).
        class:
          'border-secondary-100 bg-secondary-50/70 dark:border-secondary-200 dark:bg-secondary-100/90',
      },
      { style: 'glass', colour: 'primary', class: 'border-primary-100' },
      { style: 'glass', colour: 'info', class: 'border-tertiary-100' },
      { style: 'glass', colour: 'success', class: 'border-green-100' },
      { style: 'glass', colour: 'error', class: 'border-red-100' },
      { style: 'glass', colour: 'warning', class: 'border-orange-100' },
      { style: 'glass', colour: 'reward', class: 'border-amber-100' },
      { style: 'glass', colour: 'gold', class: 'border-deck-amber-100' },
      { style: 'glass', colour: 'silver', class: 'border-secondary-100' },
      { style: 'glass', colour: 'bronze', class: 'border-deck-orange-100' },

      // ── Solid (code-side, see header): the `-500` fill — the inversion's
      // fixed point, so the same hex in light and dark. Neutral pairs
      // `-800` with a `-50` foreground instead (both invert together). The
      // podium trio has no entry: it is already solid, and keeps that look.
      // After the colour axis so twMerge keeps the `-500` over the `-50`. ──
      { style: 'solid', colour: 'neutral', class: 'bg-secondary-800' },
      { style: 'solid', colour: 'primary', class: 'bg-primary-500' },
      { style: 'solid', colour: 'info', class: 'bg-tertiary-500' },
      { style: 'solid', colour: 'success', class: 'bg-green-500' },
      { style: 'solid', colour: 'error', class: 'bg-red-500' },
      { style: 'solid', colour: 'warning', class: 'bg-orange-500' },
      { style: 'solid', colour: 'reward', class: 'bg-amber-500' },

      // Count keeps a full pill radius at every size (overrides the size
      // axis radius — placed in compounds so it wins).
      { type: 'count', class: 'rounded-full' },

      // ── Per-(type, size) geometry — padding / gap / width. Height + radius
      // come from the size axis. Horizontal padding is a FLAT 8 (px-2 / pl-2)
      // at every size, confirmed from the per-size Label + icon-label nodes;
      // gap (6→8) and trailing pad (12→16) step up once, from XS to S, and
      // stay flat through M and L (nodes 1752:3956 / 1752:3936). ──
      // Label — symmetric flat px-2.
      { type: 'label', size: 'xxs', class: 'px-2' },
      { type: 'label', size: 'xs', class: 'px-2' },
      { type: 'label', size: 's', class: 'px-2' },
      { type: 'label', size: 'm', class: 'px-2' },
      { type: 'label', size: 'l', class: 'px-2' },

      // Icon + label — flat pl-2 and flat gap-1; only the trailing pad steps
      // up after XS (xxs keeps the xs frame).
      { type: 'icon-label', size: 'xxs', class: 'gap-1 pl-2 pr-3' },
      { type: 'icon-label', size: 'xs', class: 'gap-1 pl-2 pr-3' },
      { type: 'icon-label', size: 's', class: 'gap-1 pl-2 pr-4' },
      { type: 'icon-label', size: 'm', class: 'gap-1 pl-2 pr-4' },
      { type: 'icon-label', size: 'l', class: 'gap-1 pl-2 pr-4' },

      // Status — leading dot, same frame as icon-label (1752:3961 / 3830).
      { type: 'status', size: 'xxs', class: 'gap-1 pl-2 pr-3' },
      { type: 'status', size: 'xs', class: 'gap-1 pl-2 pr-3' },
      { type: 'status', size: 's', class: 'gap-1 pl-2 pr-4' },
      { type: 'status', size: 'm', class: 'gap-1 pl-2 pr-4' },
      { type: 'status', size: 'l', class: 'gap-1 pl-2 pr-4' },

      // Count — circular pill (min-w = height → circle for one digit, grows
      // for multi-digit). px-[6px] per the Count nodes 1752:3848/3964/3926.
      { type: 'count', size: 'xxs', class: 'min-w-5 px-1.5' },
      { type: 'count', size: 'xs', class: 'min-w-6 px-1.5' },
      { type: 'count', size: 's', class: 'min-w-7 px-1.5' },
      { type: 'count', size: 'm', class: 'min-w-8 px-1.5' },
      { type: 'count', size: 'l', class: 'min-w-9 px-1.5' },

      // Icon badge — square (width matches the size-axis height). The icon
      // centres via the base's items-/justify-center, so no padding needed.
      { type: 'icon', size: 'xxs', class: 'w-5' },
      { type: 'icon', size: 'xs', class: 'w-6' },
      { type: 'icon', size: 's', class: 'w-7' },
      { type: 'icon', size: 'm', class: 'w-8' },
      { type: 'icon', size: 'l', class: 'w-9' },

      // ── Disabled: pure opacity wash over the default colours (Figma node
      // 1752:4571). The View primitive sets no competing opacity, so a plain
      // (non-`!`) class suffices; icon / text / dot inherit the container's
      // opacity on both platforms. ────────────────────────────────────────────
      { disabled: true, class: 'opacity-50' },
    ],
    defaultVariants: {
      colour: 'neutral',
      type: 'label',
      size: 'xs',
      style: 'filled',
      disabled: false,
    },
  },
);

/**
 * Foreground (text / count / icon-via-currentColor) classes per colour + size.
 * Applied to the inner `<Text>` AND merged onto the container so web icons
 * inherit the tint through CSS `currentColor` — same pattern as Button / Chip.
 *
 * Typography: Mona Sans Bold (Figma styles L4 10 / L3 12 / L2 14). Uses
 * `!leading-[normal]` (not the Figma fixed line-heights) so RN's Text uses font
 * metrics and avoids native ascender clipping — line-height is visually
 * irrelevant in the fixed-height, single-line chip. `native:font-mona-bold`
 * pins the bold .ttf on native (no-op on web).
 *
 * `xxs` is the exception (code-side, see the header): 11px SEMIBOLD, stated
 * with its own `native:font-mona-semibold` so the native face swaps with the
 * weight. Both replace the base's bold pair through twMerge — Badge.tsx always
 * composes this through `cn`.
 *
 * `style` only matters for `solid`, the one style whose foreground is not the
 * colour's `-600`: white on the `-500` fill, and neutral's `-50` on its `-800`
 * (see the header). Podium colours keep their `-800`.
 */
export const badgeTextVariants = cva(
  'whitespace-nowrap font-bold !leading-[normal] native:font-mona-bold',
  {
    variants: {
      colour: {
        // `-600` foreground; the CSS-var palette auto-inverts it in dark mode
        // (resolves to the light `-400` hex, matching Figma frame 2812:47477).
        neutral: 'text-secondary-600 ',
        primary: 'text-primary-600 ',
        info: 'text-tertiary-600 ',
        success: 'text-green-600 ',
        error: 'text-red-600 ',
        warning: 'text-orange-600 ',
        reward: 'text-amber-600 ',
        // Podium: `-800` on a solid fill, not `-600` on a wash.
        gold: 'text-deck-amber-800 ',
        silver: 'text-subtitle-800 ',
        bronze: 'text-deck-orange-800 ',
      },
      // Font per size (confirmed from the Label nodes): L4 10 / L3 12 / L3 12 /
      // L2 14 — XS and S share neither, but S and M BOTH use 12px.
      size: {
        xxs: 'text-[11px] font-semibold native:font-mona-semibold',
        xs: 'text-[10px]',
        s: 'text-xs',
        m: 'text-xs',
        l: 'text-sm',
      },
      style: { filled: '', ghost: '', glass: '', solid: '' },
      disabled: { true: '', false: '' },
    },
    compoundVariants: [
      { style: 'solid', colour: 'neutral', class: 'text-secondary-50' },
      {
        style: 'solid',
        colour: ['primary', 'info', 'success', 'error', 'warning', 'reward'],
        class: 'text-white',
      },
    ],
    defaultVariants: { colour: 'neutral', size: 'xs', style: 'filled', disabled: false },
  },
);

/**
 * Status-dot background per colour + size — the leading circle on
 * `type="status"` badges. Mirrors the foreground colour (a `bg-*`, so it can't
 * reuse `badgeTextVariants`). Cross-platform: `bg-*` works on both web and
 * native, so the dot needs no per-platform hex resolution. Sizes: 6px (xxs /
 * xs) → 8px (s/m/l), per the Status nodes 1752:3966 / 3965 / 3961 / 3830. On a
 * `solid` badge the dot takes the solid foreground, like the text.
 */
export const badgeDotVariants = cva('shrink-0 rounded-full', {
  variants: {
    colour: {
      neutral: 'bg-secondary-600 ',
      primary: 'bg-primary-600 ',
      info: 'bg-tertiary-600 ',
      success: 'bg-green-600 ',
      error: 'bg-red-600 ',
      warning: 'bg-orange-600 ',
      reward: 'bg-amber-600 ',
      gold: 'bg-deck-amber-800 ',
      silver: 'bg-subtitle-800 ',
      bronze: 'bg-deck-orange-800 ',
    },
    size: {
      xxs: 'size-1.5',
      xs: 'size-1.5',
      s: 'size-2',
      m: 'size-2',
      l: 'size-2',
    },
    style: { filled: '', ghost: '', glass: '', solid: '' },
  },
  compoundVariants: [
    { style: 'solid', colour: 'neutral', class: 'bg-secondary-50' },
    {
      style: 'solid',
      colour: ['primary', 'info', 'success', 'error', 'warning', 'reward'],
      class: 'bg-white',
    },
  ],
  defaultVariants: { colour: 'neutral', size: 'xs', style: 'filled' },
});

/**
 * Standalone "Dot indicator badge" (Figma frame 1780:1872 light /
 * 2812:48818 dark) — a bare notification dot with its own size scale
 * (small 6 / medium 8 / large 10), rendered by {@link BadgeDot}. Same
 * `-600` fill as the status dot (the palette auto-inverts in dark mode,
 * matching the dark frame's resolved values).
 */
export const badgeDotIndicatorVariants = cva('shrink-0 rounded-full', {
  variants: {
    colour: {
      neutral: 'bg-secondary-600 ',
      primary: 'bg-primary-600 ',
      info: 'bg-tertiary-600 ',
      success: 'bg-green-600 ',
      error: 'bg-red-600 ',
      warning: 'bg-orange-600 ',
      reward: 'bg-amber-600 ',
      gold: 'bg-deck-amber-800 ',
      silver: 'bg-subtitle-800 ',
      bronze: 'bg-deck-orange-800 ',
    },
    size: {
      small: 'size-1.5',
      medium: 'size-2',
      large: 'size-2.5',
    },
  },
  defaultVariants: { colour: 'neutral', size: 'medium' },
});

export type BadgeDotVariantProps = VariantProps<typeof badgeDotIndicatorVariants>;

export type BadgeVariantProps = VariantProps<typeof badgeVariants>;
